import { track } from '@turfchale/analytics'
import { motion, useReducedMotion } from 'motion/react'
import { useEffect, useState } from 'react'

const PHRASES = ['perfect pitch', 'ideal court', 'dream turf', 'best crease']

function TypewriterText() {
  const reduced = useReducedMotion()
  const [text, setText] = useState(reduced ? PHRASES[0] : '')
  const [phraseIdx, setPhraseIdx] = useState(0)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (reduced) return
    const phrase = PHRASES[phraseIdx]
    let t: ReturnType<typeof setTimeout>

    if (!deleting) {
      if (text.length < phrase.length) {
        t = setTimeout(() => setText(phrase.slice(0, text.length + 1)), 72)
      } else {
        t = setTimeout(() => setDeleting(true), 2400)
      }
    } else {
      if (text.length > 0) {
        t = setTimeout(() => setText(text.slice(0, -1)), 42)
      } else {
        t = setTimeout(() => {
          setDeleting(false)
          setPhraseIdx((i) => (i + 1) % PHRASES.length)
        }, 320)
      }
    }

    return () => clearTimeout(t)
  }, [text, deleting, phraseIdx, reduced])

  return (
    <em className="not-italic" style={{ color: 'var(--lime)' }}>
      {text}
      {!reduced && (
        <span
          className="inline-block w-[3px] ml-[2px] rounded-sm animate-[cursor-blink_0.75s_step-end_infinite]"
          style={{ background: 'var(--lime)', height: '0.82em', verticalAlign: 'middle' }}
        />
      )}
    </em>
  )
}

const DATES = ['Today', 'Tomorrow', 'Thu 5 Jun', 'Fri 6 Jun', 'Sat 7 Jun']
const SLOTS = [
  { taken: true, time: '6 AM' },
  { taken: true, time: '7 AM' },
  { taken: false, time: '8 AM' },
  { taken: false, time: '9 AM' },
  { taken: false, time: '10 AM' },
  { taken: true, time: '11 AM' },
  { taken: false, time: '12 PM' },
  { taken: true, time: '5 PM' },
  { taken: true, time: '6 PM' },
  { taken: false, time: '7 PM' },
]

export function Hero() {
  const [selDate, setSelDate] = useState(0)
  const [selSlot, setSelSlot] = useState<null | number>(3)
  const reduced = useReducedMotion()

  const fadeUp = (delay = 0) => ({
    animate: reduced ? {} : { opacity: 1, y: 0 },
    initial: reduced ? false : { opacity: 0, y: 24 },
    transition: { delay, duration: 0.5, ease: 'easeOut' as const },
  })

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
      {/* LEFT */}
      <div>
        {/* Badge */}
        <motion.div
          {...fadeUp(0)}
          className="inline-flex items-center gap-2 text-[0.7rem] font-bold tracking-[0.09em] uppercase px-4 py-1.5 rounded-full mb-7"
          style={{
            background: 'var(--lime-dim)',
            border: '1px solid var(--lime-line)',
            color: 'var(--lime)',
          }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full animate-[pulse-dot_2s_infinite]"
            style={{ background: 'var(--lime)' }}
          />
          500+ turfs live across India
        </motion.div>

        <motion.h1
          {...fadeUp(0.1)}
          className="font-display font-bold leading-[1.04] tracking-[-1.5px] mb-5"
          style={{ color: 'var(--text)', fontSize: 'clamp(2.6rem,5.5vw,4rem)' }}
        >
          Book the <TypewriterText />
          <br />
          in seconds.
        </motion.h1>

        <motion.p
          {...fadeUp(0.2)}
          className="text-base font-light leading-[1.75] max-w-115 mb-10"
          style={{ color: 'var(--muted)' }}
        >
          TurfChale makes finding and booking football, cricket, and badminton turfs near you
          completely effortless. Real-time slots, instant payment, zero commission for players.
        </motion.p>

        <motion.div {...fadeUp(0.3)} className="flex flex-wrap gap-3">
          <a
            className="inline-flex items-center gap-1 font-bold text-[0.95rem] px-7 py-3 rounded-full transition-transform duration-150 hover:scale-[1.04]"
            href="/turfs"
            onClick={() => track('landing_cta_clicked', { cta: 'hero' })}
            style={{ background: 'var(--lime)', color: 'var(--bg)' }}
          >
            Book Your Turf →
          </a>
          <a
            className="inline-flex items-center gap-1 text-[0.95rem] px-7 py-3 rounded-full border transition-colors duration-200"
            href="https://portal.turfchale.com"
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,.45)')}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,.18)')}
            rel="noopener noreferrer"
            style={{ borderColor: 'rgba(255,255,255,.18)', color: 'var(--text)' }}
            target="_blank"
          >
            List Your Turf
          </a>
        </motion.div>
      </div>

      {/* RIGHT — Booking Card */}
      <motion.div
        animate={reduced ? {} : { opacity: 1, x: 0 }}
        className="relative"
        initial={reduced ? false : { opacity: 0, x: 36 }}
        transition={{ delay: 0.2, duration: 0.55, ease: 'easeOut' }}
      >
        {/* Toast */}
        <div
          className="absolute top-3 left-3 z-10 flex items-center gap-2 px-3 py-2 rounded-xl animate-[toast-in_.5s_ease_.7s_both]"
          style={{ background: 'rgba(11,28,16,.93)', border: '1px solid var(--lime-line)' }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full shrink-0 animate-[pulse-dot_2s_infinite]"
            style={{ background: 'var(--lime)' }}
          />
          <p className="text-[0.72rem] leading-tight" style={{ color: 'var(--muted)' }}>
            <strong className="block" style={{ color: 'var(--text)' }}>
              Just booked!
            </strong>
            Sai Arena, Andheri · 6 PM slot
          </p>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl overflow-hidden"
          style={{ background: 'var(--bg3)', border: '1px solid var(--lime-line)' }}
        >
          {/* Header */}
          <div
            className="flex justify-between items-start px-6 py-5"
            style={{ borderBottom: '1px solid var(--lime-line)' }}
          >
            <div>
              <div
                className="font-display font-semibold text-[1.1rem] mb-0.5"
                style={{ color: 'var(--text)' }}
              >
                Champions Ground
              </div>
              <div className="text-[0.75rem]" style={{ color: 'var(--muted)' }}>
                📍 Powai, Mumbai
              </div>
            </div>
            <div className="text-right">
              <div
                className="font-display font-bold text-[1.3rem]"
                style={{ color: 'var(--lime)' }}
              >
                ₹850{' '}
                <span
                  className="text-[0.7rem] font-normal font-body"
                  style={{ color: 'var(--muted)' }}
                >
                  / hr
                </span>
              </div>
              <div
                className="text-[0.7rem] font-bold px-2.5 py-0.5 rounded-full mt-1 inline-block"
                style={{ background: 'var(--lime-dim)', color: 'var(--lime)' }}
              >
                ● 3 open
              </div>
            </div>
          </div>

          {/* Pitch SVG */}
          <div
            className="h-25 flex items-center justify-center relative"
            style={{ background: '#142919' }}
          >
            <svg className="w-full h-full opacity-20" fill="none" viewBox="0 0 380 100">
              <rect height="88" stroke="#b8ec3c" strokeWidth="1.5" width="348" x="16" y="6" />
              <line stroke="#b8ec3c" x1="190" x2="190" y1="6" y2="94" />
              <circle cx="190" cy="50" r="26" stroke="#b8ec3c" />
              <rect height="44" stroke="#b8ec3c" width="28" x="16" y="28" />
              <rect height="44" stroke="#b8ec3c" width="28" x="336" y="28" />
              <rect height="64" stroke="#b8ec3c" width="52" x="16" y="18" />
              <rect height="64" stroke="#b8ec3c" width="52" x="312" y="18" />
            </svg>
            <span
              className="absolute bottom-2 right-3 text-[0.6rem] tracking-[0.08em] uppercase opacity-50"
              style={{ color: 'var(--lime)' }}
            >
              Football · 5-a-side
            </span>
          </div>

          {/* Date tabs */}
          <div
            className="flex gap-1.5 px-5 py-3.5 overflow-x-auto scrollbar-none"
            style={{ scrollbarWidth: 'none' }}
          >
            {DATES.map((d, i) => (
              <button
                className="shrink-0 text-[0.72rem] px-3.5 py-1.5 rounded-full border transition-all duration-150"
                key={d}
                onClick={() => setSelDate(i)}
                style={
                  selDate === i
                    ? {
                        background: 'var(--lime)',
                        borderColor: 'var(--lime)',
                        color: 'var(--bg)',
                        fontWeight: 700,
                      }
                    : { borderColor: 'var(--lime-line)', color: 'var(--muted)' }
                }
              >
                {d}
              </button>
            ))}
          </div>

          {/* Slot grid */}
          <p
            className="px-5 pb-1 text-[0.67rem] tracking-[0.08em] uppercase"
            style={{ color: 'var(--text-dim)' }}
          >
            Available slots
          </p>
          <div className="grid grid-cols-5 gap-1.5 px-5 pb-4">
            {SLOTS.map((s, i) => (
              <button
                className="rounded-lg py-1.5 text-center text-[0.68rem] border transition-all duration-150"
                disabled={s.taken}
                key={i}
                onClick={() => !s.taken && setSelSlot(i)}
                style={
                  s.taken
                    ? {
                        borderColor: 'rgba(255,255,255,.06)',
                        color: 'var(--text-dim)',
                        cursor: 'default',
                        textDecoration: 'line-through',
                      }
                    : selSlot === i
                      ? {
                          background: 'var(--lime)',
                          borderColor: 'var(--lime)',
                          color: 'var(--bg)',
                          fontWeight: 700,
                        }
                      : { borderColor: 'var(--lime-line)', color: 'var(--lime)' }
                }
              >
                {s.time}
              </button>
            ))}
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-between px-5 py-4"
            style={{ borderTop: '1px solid var(--lime-line)' }}
          >
            <div className="text-[0.78rem]" style={{ color: 'var(--muted)' }}>
              {selSlot !== null ? (
                <>
                  {SLOTS[selSlot].time} ·{' '}
                  <strong style={{ color: 'var(--lime)', fontSize: '0.9rem' }}>₹850</strong>
                </>
              ) : (
                'Select a slot'
              )}
            </div>
            <motion.button
              className="text-[0.875rem] font-bold px-5 py-2.5 rounded-full transition-transform hover:scale-[1.04]"
              style={{ background: 'var(--lime)', border: 'none', color: 'var(--bg)' }}
              whileTap={reduced ? {} : { scale: 0.96 }}
            >
              Confirm Booking
            </motion.button>
          </div>
        </div>
      </motion.div>
    </section>
  )
}
